import { observer } from "mobx-react-lite"
import { Modal, useDisclose, View } from "native-base"
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Dimensions, ViewStyle } from "react-native"
import {
  Button,
  Image,
  Text,
  OccupationInputForm,
} from "../../../components"
import { User } from "../../../database"
import { useStores } from "../../../models"
import { color, images } from "../../../theme"
import SyncableModel from "../../../database/SyncableModel"
import { FormikProps, FormikValues } from "formik"
import { translate } from "../../../i18n"
import JalaAnalytic from "../../../utils/jala-analytic"

const OCCUPATION_IMAGE = {
  _height: 167,
  _width: 300,
  width: Dimensions.get('window').width - 64,
  get height() {
    return this._height * (this.width / this._width)
  }
}

const BUTTON: ViewStyle = {
  marginVertical: 16,
  backgroundColor: color.primary
}

const FIRST_MODAL_BUTTON: ViewStyle = {
  paddingHorizontal: 32,
  paddingVertical: 8,
  backgroundColor: color.primary
}

export const InputProfileModal: FC<{ colorScheme?: "light"|"dark" }> = observer(function InputProfileModal(props) {
  const { colorScheme } = props
  const { isOpen, onClose } = useDisclose(true)
  const {
    userStore, userStore: { user, loading: userLoading },
    occupationStore, occupationStore: { occupations, loading }
  } = useStores()
  const [ isForm, setIsForm ] = useState(false)
  const [ model, setModel ] = useState<SyncableModel>(null)
  const [ isError, setIsError ] = useState<boolean>(true)

  const profileForm = useRef<FormikProps<FormikValues>>()
  const profileFormRef = useCallback((node) => {
    profileForm.current = node;
    if (profileForm.current) setIsError(!(profileForm.current.dirty && profileForm.current.isValid))
  }, [])

  useEffect(() => {
    if (user) {
      const model = User.hydrate({ ...user })
      setModel(model)
    }
  }, [setModel, user])

  const initialValues = useMemo(() => {
    const data = (model ? model.toArray() : user)
    return { ...data, state: { ...data.state } }
  },[model, user])

  const onNext = useCallback(() => {
    setIsForm(true);
    JalaAnalytic.log('Mobile App : Pop up reminder complete data profile', user);
  }, [user])

  const onSubmit = useCallback((values) => {
    if (userLoading) return;
    userStore.update(model, values)
      .then(() => userStore.getUser())
      .then(() => {
        JalaAnalytic.log('Mobile App : Save Form Complete occupation data', values);
        onClose();
      });
  }, [model, onClose, userLoading, userStore])

  const handleSubmit = useCallback((event) => {
    if (profileForm.current) profileForm.current.handleSubmit(event)
  }, [])

  const getOccupation = useCallback(() => {
    occupationStore.getOccupations().catch()
  }, [occupationStore])

  return (
    <Modal
      testID="jala:id/onboard-occupation-modal"
      isOpen={isOpen}
      // onClose={onClose}
      animationPreset="slide"
    >
      <View
        alignSelf="stretch"
        marginY="80px"
        marginX="16px"
        padding="16px"
        rounded="8px"
        backgroundColor={color.palette.white}
      >
        {!isForm ? <View>
          <Image source={images.occupation} style={OCCUPATION_IMAGE} resizeMode="contain" />
          <Text preset="title1" text={translate("homeScreen.occupationModalTitle", {user: user.name})} marginTop="16px" marginBottom="8px" colorScheme={colorScheme} color={color.palette.gray900} textAlign="center" />
          <Text preset="body2" tx="homeScreen.occupationModalSubtitle" marginBottom="16px" colorScheme={colorScheme} color={color.palette.gray800} textAlign="center" mx="10px" />
          <View alignItems="center" marginTop="18px">
            <Button
              testID="jala:id/action-button"
              tx="common.next"
              colorScheme={colorScheme}
              onPress={onNext}
              style={FIRST_MODAL_BUTTON}
              textColor={color.palette.white}
            />
          </View>
        </View> : <>
          <OccupationInputForm
            initialValues={initialValues}
            onSubmit={onSubmit}
            getOccupation={getOccupation}
            occupations={occupations}
            loading={loading}
            innerRef={profileFormRef}
          />
          <Button
            testID="jala:id/save-profile-button"
            tx="save"
            colorScheme={colorScheme}
            style={BUTTON}
            disabled={isError}
            loading={userLoading}
            onPress={handleSubmit}
            textColor={color.palette.white}
          />
        </>}
      </View>
    </Modal>
  )
})
